import { Button, Select } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { coLocAi, type LocAi, type TruongLocAi, type TuyChonAiManHinh } from '../../chung/nghiep-vu/loc-ai';
import type { TrangThaiGia } from '../../chung/nghiep-vu/types-vet';

type TruongChuoi = Exclude<TruongLocAi, 'trangThai' | 'coCanhBao'>;

const O_CHUOI: ReadonlyArray<{
  truong: TruongChuoi;
  nguon: keyof Omit<TuyChonAiManHinh, 'biCat'>;
  nhan: KhoaDich;
  rong: string;
}> = [
  { truong: 'moHinh', nguon: 'moHinh', nhan: 'nktvSuDungAi.loc.moHinh', rong: 'w-52' },
  { truong: 'nhaCungCap', nguon: 'nhaCungCap', nhan: 'nktvSuDungAi.loc.nhaCungCap', rong: 'w-40' },
  { truong: 'service', nguon: 'service', nhan: 'nktvSuDungAi.loc.service', rong: 'w-44' },
  { truong: 'tinhNang', nguon: 'tinhNang', nhan: 'nktvSuDungAi.loc.tinhNang', rong: 'w-48' },
  { truong: 'nguoiDungId', nguon: 'nguoi', nhan: 'nktvSuDungAi.loc.nguoiDung', rong: 'w-48' },
  { truong: 'donViId', nguon: 'donVi', nhan: 'nktvSuDungAi.loc.donVi', rong: 'w-48' },
];

/** `Select` của antd không giữ được `false` làm giá trị chọn rõ ràng — đổi qua hai mã chuỗi. */
type ChonCanhBao = 'co' | 'khong';

/**
 * Hàng ô lọc của màn Sử dụng AI — port `vet/BoLocAi.tsx` native. Chỉ vẽ các ô trong `truong` — tập ô mà endpoint của
 * tab đang xem thật sự áp (`TRUONG_THEO_TAB`): một ô hiện mà không lọc là một ô nói dối.
 *
 * "Xoá bộ lọc" xoá MỌI ô, kể cả ô đang ẩn ở tab này: để lại một ô ẩn vẫn đặt thì sang tab khác số liệu bị lọc theo một
 * điều kiện người dùng tưởng đã xoá.
 */
export function BoLocAi({
  loc,
  onDoi,
  chon,
  truong,
  loiGoiY,
}: {
  loc: LocAi;
  onDoi: (loc: LocAi) => void;
  chon: TuyChonAiManHinh;
  truong: readonly TruongLocAi[];
  loiGoiY: boolean;
}) {
  const { t } = useT();
  if (truong.length === 0) return null;
  const hien = new Set(truong);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {O_CHUOI.filter((o) => hien.has(o.truong)).map((o) => (
          <Select<string | undefined>
            key={o.truong}
            aria-label={t(o.nhan)}
            placeholder={t(o.nhan)}
            value={loc[o.truong]}
            onChange={(v) => onDoi({ ...loc, [o.truong]: v })}
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            className={`${o.rong} max-w-full`}
            options={chon[o.nguon]}
          />
        ))}
        {hien.has('trangThai') && (
          <Select<TrangThaiGia | undefined>
            aria-label={t('nktvSuDungAi.loc.trangThai')}
            placeholder={t('nktvSuDungAi.loc.trangThai')}
            value={loc.trangThai}
            onChange={(trangThai) => onDoi({ ...loc, trangThai })}
            allowClear
            className="w-40 max-w-full"
            options={[
              { value: 'da_tinh', label: t('nktvSuDungAi.loc.trangThaiDaTinh') },
              { value: 'chua_co_gia', label: t('nktvSuDungAi.loc.trangThaiChuaCoGia') },
            ]}
          />
        )}
        {hien.has('coCanhBao') && (
          <Select<ChonCanhBao | undefined>
            aria-label={t('nktvSuDungAi.loc.coCanhBao')}
            placeholder={t('nktvSuDungAi.loc.coCanhBao')}
            value={loc.coCanhBao === undefined ? undefined : loc.coCanhBao ? 'co' : 'khong'}
            onChange={(v) => onDoi({ ...loc, coCanhBao: v === undefined ? undefined : v === 'co' })}
            allowClear
            className="w-40 max-w-full"
            options={[
              { value: 'co', label: t('nktvSuDungAi.loc.coCanhBaoCo') },
              { value: 'khong', label: t('nktvSuDungAi.loc.coCanhBaoKhong') },
            ]}
          />
        )}
        {coLocAi(loc, truong) && (
          <Button variant="ghost" onClick={() => onDoi({})}>
            {t('nktv.loc.xoaLoc')}
          </Button>
        )}
      </div>
      {chon.biCat && (
        <p className="text-xs text-warning-700" role="status">
          {t('nktv.loc.biCat')}
        </p>
      )}
      {loiGoiY && (
        <p className="text-xs text-content-muted" role="status">
          {t('nktvSuDungAi.loc.loiGoiY')}
        </p>
      )}
    </div>
  );
}
